import React, { useState, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
  Select,
  Input,
  Button,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Tooltip,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Spinner,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  FiSearch,
  FiFilter,
  FiDownload,
  FiRefreshCw,
  FiExternalLink,
  FiEye,
  FiCalendar,
  FiDollarSign,
  FiTrendingUp,
  FiTrendingDown,
  FiHash,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiInfo,
  FiArrowLeft,
} from 'react-icons/fi';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  ReferenceLine,
  Area,
  AreaChart,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { useTranslation } from 'react-i18next';
import { formatDOR, formatUSD, formatAddress, formatDate } from '@/utils/formatters';
import { usePriceData } from '@/hooks/usePriceData';
import ConnectButton from '@/components/common/ConnectButton';
import PageHeader from '@/components/common/PageHeader';

// 交易类型定义
interface Transaction {
  id: string;
  timestamp: string;
  type: 'recharge' | 'convert' | 'destroy' | 'sell' | 'invite' | 'refund';
  amount: number;
  unit: 'DOR' | 'USDT';
  valuation?: number; // 当时估值
  details: string;
  status: 'completed' | 'pending' | 'failed';
  hash: string;
  blockNumber: number;
  gasUsed: number;
  relatedAddress?: string;
  priceAtTime?: number;
}

// 交易详情模态组件
interface TransactionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
}

const TransactionDetailModal: React.FC<TransactionDetailModalProps> = ({
  isOpen,
  onClose,
  transaction,
}) => {
  const { t } = useTranslation('history');
  
  if (!transaction) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('modal.transactionDetails')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            <SimpleGrid columns={2} spacing={4}>
              <Box>
                <Text fontSize="sm" color="gray.500">{t('modal.transactionHash')}</Text>
                <Text fontFamily="mono" fontSize="sm">
                  {formatAddress(transaction.hash)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">{t('modal.blockNumber')}</Text>
                <Text>{transaction.blockNumber}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">{t('modal.gasFee')}</Text>
                <Text>{transaction.gasUsed} wei</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">{t('modal.status')}</Text>
                <Badge
                  colorScheme={
                    transaction.status === 'completed' ? 'green' :
                    transaction.status === 'pending' ? 'yellow' : 'red'
                  }
                >
                  {transaction.status === 'completed' ? t('status.completed') :
                   transaction.status === 'pending' ? t('status.pending') : t('status.failed')}
                </Badge>
              </Box>
            </SimpleGrid>
            
            {transaction.relatedAddress && (
              <Box>
                <Text fontSize="sm" color="gray.500">{t('modal.relatedAddress')}</Text>
                <Text fontFamily="mono" fontSize="sm">
                  {formatAddress(transaction.relatedAddress)}
                </Text>
              </Box>
            )}
            
            {transaction.priceAtTime && (
              <Box>
                <Text fontSize="sm" color="gray.500">{t('modal.priceAtTime')}</Text>
                <Text>{formatUSD(transaction.priceAtTime)} USDT</Text>
              </Box>
            )}
            
            <Button
              leftIcon={<FiExternalLink />}
              variant="outline"
              size="sm"
              onClick={() => window.open(`https://bscscan.com/tx/${transaction.hash}`, '_blank')}
            >
              {t('modal.viewOnBscscan')}
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// 所有交易模块组件
const AllTransactions: React.FC<{ currentPrice: number }> = ({ currentPrice }) => {
  const { t } = useTranslation('history');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const hoverBg = useColorModeValue('gray.50', 'gray.600');

  // 模拟交易数据
  const mockTransactions: Transaction[] = [
    {
      id: '1',
      timestamp: '2025-01-15 14:30:00',
      type: 'recharge',
      amount: 100,
      unit: 'DOR',
      valuation: 99,
      details: 'USDT充值，获得100 DOR',
      status: 'completed',
      hash: '0x1234567890abcdef1234567890abcdef12345678',
      blockNumber: 12345678,
      gasUsed: 21000,
      priceAtTime: 0.99,
    },
    {
      id: '2',
      timestamp: '2025-01-14 16:45:00',
      type: 'convert',
      amount: 50,
      unit: 'DOR',
      details: '流通DOR转换为锁定DOR',
      status: 'completed',
      hash: '0xabcdef1234567890abcdef1234567890abcdef12',
      blockNumber: 12345677,
      gasUsed: 180000,
      priceAtTime: 1.02,
    },
    {
      id: '3',
      timestamp: '2025-01-13 09:15:00',
      type: 'destroy',
      amount: 25,
      unit: 'DOR',
      valuation: 24.75,
      details: '锁定DOR销毁，获得返现',
      status: 'completed',
      hash: '0x567890abcdef1234567890abcdef1234567890ab',
      blockNumber: 12345676,
      gasUsed: 150000,
      priceAtTime: 0.99,
    },
    {
      id: '4',
      timestamp: '2025-01-12 11:20:00',
      type: 'sell',
      amount: 30,
      unit: 'DOR',
      valuation: 29.7,
      details: '外盘卖出DOR',
      status: 'completed',
      hash: '0x90abcdef1234567890abcdef1234567890abcdef',
      blockNumber: 12345675,
      gasUsed: 200000,
      priceAtTime: 0.99,
    },
    {
      id: '5',
      timestamp: '2025-01-11 13:10:00',
      type: 'invite',
      amount: 10,
      unit: 'USDT',
      details: '邀请用户返现',
      status: 'completed',
      hash: '0xdef1234567890abcdef1234567890abcdef12345',
      blockNumber: 12345674,
      gasUsed: 120000,
      relatedAddress: '0x1234567890abcdef1234567890abcdef12345678',
    },
  ];

  // 过滤和排序交易
  const filteredTransactions = useMemo(() => {
    let filtered = mockTransactions;

    // 类型过滤
    if (filterType !== 'all') {
      filtered = filtered.filter(tx => tx.type === filterType);
    }

    // 搜索过滤
    if (searchTerm) {
      filtered = filtered.filter(tx =>
        tx.hash.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // 排序
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'timestamp':
          aValue = new Date(a.timestamp).getTime();
          bValue = new Date(b.timestamp).getTime();
          break;
        case 'amount':
          aValue = a.amount;
          bValue = b.amount;
          break;
        case 'valuation':
          aValue = a.valuation || 0;
          bValue = b.valuation || 0;
          break;
        default:
          aValue = a.timestamp;
          bValue = b.timestamp;
      }

      if (sortOrder === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return filtered;
  }, [mockTransactions, filterType, searchTerm, sortBy, sortOrder]);

  // 分页
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // 统计信息
  const stats = useMemo(() => {
    const totalRecharge = mockTransactions
      .filter(tx => tx.type === 'recharge')
      .reduce((sum, tx) => sum + (tx.valuation || 0), 0);
    
    const totalRefund = mockTransactions
      .filter(tx => tx.type === 'destroy' || tx.type === 'invite')
      .reduce((sum, tx) => sum + (tx.valuation || tx.amount), 0);
    
    const totalConvert = mockTransactions
      .filter(tx => tx.type === 'convert')
      .reduce((sum, tx) => sum + tx.amount, 0);
    
    const avgAmount = mockTransactions.length > 0 
      ? mockTransactions.reduce((sum, tx) => sum + tx.amount, 0) / mockTransactions.length 
      : 0;

    return { totalRecharge, totalRefund, totalConvert, avgAmount };
  }, [mockTransactions]);

  const handleViewDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    onOpen();
  };

  const handleExport = () => {
    toast({
      title: t('notifications.exportFeature'),
      description: t('notifications.exportFeatureDesc'),
      status: 'info',
      duration: 3000,
    });
  };

  const handleRefresh = () => {
    toast({
      title: t('notifications.refreshSuccess'),
      description: t('notifications.refreshSuccessDesc'),
      status: 'success',
      duration: 2000,
    });
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'recharge': return 'green';
      case 'convert': return 'blue';
      case 'destroy': return 'orange';
      case 'sell': return 'red';
      case 'invite': return 'purple';
      case 'refund': return 'teal';
      default: return 'gray';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'recharge': return t('transactionTypes.recharge');
      case 'convert': return t('transactionTypes.convert');
      case 'destroy': return t('transactionTypes.destroy');
      case 'sell': return t('transactionTypes.sell');
      case 'invite': return t('transactionTypes.invite');
      case 'refund': return t('transactionTypes.refund');
      default: return type;
    }
  };

  return (
    <Box>
      {/* 统计概览 */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
        <Stat>
          <StatLabel>{t('totalRecharge')}</StatLabel>
          <StatNumber>{formatUSD(stats.totalRecharge)}</StatNumber>
          <StatHelpText>USDT</StatHelpText>
        </Stat>
        <Stat>
          <StatLabel>{t('totalRefund')}</StatLabel>
          <StatNumber>{formatUSD(stats.totalRefund)}</StatNumber>
          <StatHelpText>USDT</StatHelpText>
        </Stat>
        <Stat>
          <StatLabel>{t('totalConvert')}</StatLabel>
          <StatNumber>{formatDOR(stats.totalConvert)}</StatNumber>
          <StatHelpText>DOR</StatHelpText>
        </Stat>
        <Stat>
          <StatLabel>{t('averageAmount')}</StatLabel>
          <StatNumber>{formatDOR(stats.avgAmount)}</StatNumber>
          <StatHelpText>{t('perTransaction')}</StatHelpText>
        </Stat>
      </SimpleGrid>

      {/* 过滤工具栏 */}
      <Card mb={6}>
        <CardBody>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={4}
            align={{ base: 'stretch', md: 'center' }}
          >
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              maxW={{ base: 'full', md: '200px' }}
            >
              <option value="all">{t('filterToolbar.allTypes')}</option>
              <option value="recharge">{t('filterToolbar.recharge')}</option>
              <option value="convert">{t('filterToolbar.convert')}</option>
              <option value="destroy">{t('filterToolbar.destroy')}</option>
              <option value="sell">{t('filterToolbar.sell')}</option>
              <option value="invite">{t('filterToolbar.invite')}</option>
            </Select>
            
            <Input
              placeholder={t('filterToolbar.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              maxW={{ base: 'full', md: '300px' }}
            />
            
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              maxW={{ base: 'full', md: '150px' }}
            >
              <option value="timestamp">{t('filterToolbar.sortByTime')}</option>
              <option value="amount">{t('filterToolbar.sortByAmount')}</option>
              <option value="valuation">{t('filterToolbar.sortByValuation')}</option>
            </Select>
            
            <IconButton
              aria-label={t('filterToolbar.toggleSort')}
                              icon={sortOrder === 'desc' ? <FiTrendingDown /> : <FiTrendingUp />}
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              variant="outline"
            />
            
            <Button
              leftIcon={<FiDownload />}
              onClick={handleExport}
              variant="outline"
              size="sm"
            >
              {t('filterToolbar.export')}
            </Button>
            

          </Flex>
        </CardBody>
      </Card>

      {/* 交易列表 */}
      <Card>
        <CardBody>
          {isMobile ? (
            // 移动端 Accordion 布局
            <Accordion allowToggle>
              {paginatedTransactions.map((tx) => (
                <AccordionItem key={tx.id}>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <HStack justify="space-between" align="center">
                        <VStack align="start" spacing={1}>
                          <Badge colorScheme={getTypeColor(tx.type)}>
                            {getTypeLabel(tx.type)}
                          </Badge>
                          <Text fontSize="sm" color="gray.600">
                            {formatDate(tx.timestamp)}
                          </Text>
                        </VStack>
                        <VStack align="end" spacing={1}>
                          <Text fontWeight="bold">
                            {tx.amount > 0 ? '+' : ''}{formatDOR(tx.amount)} {tx.unit}
                          </Text>
                          {tx.valuation && (
                            <Text fontSize="xs" color="gray.500">
                              {formatUSD(tx.valuation)} USDT
                            </Text>
                          )}
                        </VStack>
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <VStack align="stretch" spacing={3}>
                      <Text fontSize="sm">{tx.details}</Text>
                      <HStack justify="space-between">
                        <Badge
                          colorScheme={
                            tx.status === 'completed' ? 'green' :
                            tx.status === 'pending' ? 'yellow' : 'red'
                          }
                        >
                          {tx.status === 'completed' ? '已完成' :
                           tx.status === 'pending' ? '处理中' : '失败'}
                        </Badge>
                        <Button
                          size="xs"
                          leftIcon={<FiEye />}
                          onClick={() => handleViewDetails(tx)}
                          variant="ghost"
                        >
                          {t('actions.details')}
                        </Button>
                      </HStack>
                      <Text fontSize="xs" color="gray.500" fontFamily="mono">
                        {formatAddress(tx.hash)}
                      </Text>
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            // 桌面端表格布局
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t('tableHeaders.time')}</Th>
                  <Th>{t('tableHeaders.type')}</Th>
                  <Th>{t('tableHeaders.amount')}</Th>
                  <Th>{t('tableHeaders.details')}</Th>
                  <Th>{t('tableHeaders.status')}</Th>
                  <Th>{t('tableHeaders.actions')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedTransactions.map((tx) => (
                  <Tr key={tx.id} _hover={{ bg: hoverBg }}>
                    <Td>
                      <Text fontSize="sm">{formatDate(tx.timestamp)}</Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={getTypeColor(tx.type)}>
                        {getTypeLabel(tx.type)}
                      </Badge>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold">
                          {tx.amount > 0 ? '+' : ''}{formatDOR(tx.amount)} {tx.unit}
                        </Text>
                        {tx.valuation && (
                          <Text fontSize="sm" color="gray.600">
                            {formatUSD(tx.valuation)} USDT
                            {currentPrice < 0.99 && tx.valuation < tx.amount && (
                              <Text as="span" color="red.500" ml={2}>
                                ({t('priceDeviation')})
                              </Text>
                            )}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <Text fontSize="sm" maxW="200px" noOfLines={2}>
                        {tx.details}
                      </Text>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={
                          tx.status === 'completed' ? 'green' :
                          tx.status === 'pending' ? 'yellow' : 'red'
                        }
                      >
                        {tx.status === 'completed' ? t('status.completed') :
                         tx.status === 'pending' ? t('status.pending') : t('status.failed')}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Tooltip label={t('actions.viewDetails')}>
                          <IconButton
                            aria-label={t('actions.viewDetails')}
                            icon={<FiEye />}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewDetails(tx)}
                          />
                        </Tooltip>
                        <Tooltip label={t('actions.viewOnBscscan')}>
                          <IconButton
                            aria-label={t('actions.viewOnBscscan')}
                            icon={<FiExternalLink />}
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(`https://bscscan.com/tx/${tx.hash}`, '_blank')}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}

          {/* 分页 */}
          {totalPages > 1 && (
            <Flex justify="center" mt={6}>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  isDisabled={currentPage === 1}
                >
                  {t('pagination.previous')}
                </Button>
                <Text fontSize="sm">
                  {t('pagination.pageInfo', { current: currentPage, total: totalPages })}
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  isDisabled={currentPage === totalPages}
                >
                  {t('pagination.next')}
                </Button>
              </HStack>
            </Flex>
          )}

          {/* 空状态 */}
          {filteredTransactions.length === 0 && (
            <Box textAlign="center" py={8}>
              <Text color="gray.500" mb={4}>
                {t('emptyState.noTransactions')}
              </Text>
              <Button colorScheme="blue" onClick={() => window.location.href = '/recharge'}>
                {t('emptyState.startRecharge')}
              </Button>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* 交易详情模态 */}
      <TransactionDetailModal
        isOpen={isOpen}
        onClose={onClose}
        transaction={selectedTransaction}
      />
    </Box>
  );
};

// 图表分析模块组件
const ChartAnalysis: React.FC<{ currentPrice: number }> = ({ currentPrice }) => {
  const { t } = useTranslation('history');
  const [timeRange, setTimeRange] = useState('7d');

  // 模拟图表数据
  const chartData = [
    { date: '2025-01-09', recharge: 50, destroy: 0, sell: 0, price: 1.02 },
    { date: '2025-01-10', recharge: 30, destroy: 10, sell: 0, price: 1.01 },
    { date: '2025-01-11', recharge: 80, destroy: 5, sell: 20, price: 0.99 },
    { date: '2025-01-12', recharge: 40, destroy: 15, sell: 30, price: 0.98 },
    { date: '2025-01-13', recharge: 60, destroy: 25, sell: 10, price: 0.99 },
    { date: '2025-01-14', recharge: 70, destroy: 0, sell: 0, price: 1.00 },
    { date: '2025-01-15', recharge: 100, destroy: 0, sell: 0, price: 0.99 },
  ];

  const barData = [
    { type: '充值', amount: 430, color: '#48BB78' },
    { type: '销毁', amount: 55, color: '#ED8936' },
    { type: '卖出', amount: 60, color: '#E53E3E' },
    { type: '转换', amount: 200, color: '#3182CE' },
  ];

  return (
    <Box>
      {/* 时间范围选择 */}
      <Card mb={6}>
        <CardBody>
          <Flex justify="space-between" align="center">
            <Text fontWeight="bold">{t('chartAnalysis.timeRange')}</Text>
            <HStack spacing={2}>
              {['7d', '30d', 'all'].map((range) => (
                <Button
                  key={range}
                  size="sm"
                  variant={timeRange === range ? 'solid' : 'outline'}
                  onClick={() => setTimeRange(range)}
                >
                  {t(`chartAnalysis.timeRanges.${range}`)}
                </Button>
              ))}
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* 交易趋势图 */}
      <Card mb={6}>
        <CardBody>
          <Text fontSize="lg" fontWeight="bold" mb={4}>{t('chartAnalysis.transactionTrend')}</Text>
          <Box h="300px">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="recharge"
                  stackId="1"
                  stroke="#48BB78"
                  fill="#48BB78"
                  name={t('chartAnalysis.chartLabels.recharge')}
                />
                <Area
                  type="monotone"
                  dataKey="destroy"
                  stackId="1"
                  stroke="#ED8936"
                  fill="#ED8936"
                  name={t('chartAnalysis.chartLabels.destroy')}
                />
                <Area
                  type="monotone"
                  dataKey="sell"
                  stackId="1"
                  stroke="#E53E3E"
                  fill="#E53E3E"
                  name={t('chartAnalysis.chartLabels.sell')}
                />
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke="#3182CE"
                  strokeWidth={2}
                  name={t('chartAnalysis.chartLabels.price')}
                  yAxisId={1}
                />

              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </CardBody>
      </Card>

      {/* 交易类型分布 */}
      <Card>
        <CardBody>
          <Text fontSize="lg" fontWeight="bold" mb={4}>{t('chartAnalysis.transactionDistribution')}</Text>
          <Box h="300px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" />
                <YAxis />
                <RechartsTooltip />
                <Bar dataKey="amount" fill="#3182CE" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardBody>
      </Card>
    </Box>
  );
};

// 主组件
const TransactionHistory: React.FC = () => {
  const { t } = useTranslation('history');
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const { priceData } = usePriceData();
  const currentPrice = priceData?.currentPrice || 1.0;
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // 渲染内容
  const renderContent = () => {
    // 未连接钱包时的提示
    if (!isConnected) {
      return (
        <Box>
          <VStack spacing={6} align="stretch">
            {/* 顶部区域 */}
            <PageHeader title={t('title')} />

            <Card>
              <CardBody>
                <VStack spacing={4}>
                  <Text color="gray.600">
                    {t('connectWalletPrompt')}
                  </Text>
                  <ConnectButton />
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </Box>
      );
    }

      return renderContent();
  };

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        {/* 顶部区域 */}
        <PageHeader 
          title={t('title')} 
          subtitle={t('subtitle')}
        />

        {/* 主要内容 */}
        <Tabs variant="enclosed">
          <TabList>
            <Tab>{t('allTransactions')}</Tab>
            <Tab>{t('tabChartAnalysis')}</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <AllTransactions currentPrice={currentPrice} />
            </TabPanel>
            <TabPanel>
              <ChartAnalysis currentPrice={currentPrice} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  );
};

export default TransactionHistory; 